import { useCallback, useState } from 'react';
import {
  Add,
  Close,
  ContentCopy,
  Delete,
  Edit,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { LoadingState } from './loading-state';
import {
  useGetAdminProviderEncounterTypesQuery,
  useCreateAdminProviderEncounterTypeMutation,
  useUpdateAdminProviderEncounterTypeMutation,
  useDeleteAdminProviderEncounterTypeMutation,
} from '../store/hooks';
import type {
  EncounterTypeCreate,
  EncounterTypeResponse,
  EncounterTypeUpdate,
} from '@store/generated/admin';
import type { EffectiveNoteTemplateSummary } from '@store/generated/templates';

interface ProviderEncounterTypesSectionProps {
  providerId: string;
  templates?: EffectiveNoteTemplateSummary[];
}

type EncounterTypeFormData = EncounterTypeUpdate & { encounter_type?: string | null };

const MODALITY_LABELS: Record<string, string> = {
  in_person: 'In Person',
  video: 'Video',
  audio: 'Audio',
};

const COLOR_MAP: Record<string, string> = {
  color1: '#4A90D9',
  color2: '#E57373',
  color3: '#81C784',
  color4: '#FFB74D',
  color5: '#BA68C8',
  color6: '#4DB6AC',
  color7: '#FF8A65',
  color8: '#A1887F',
  color9: '#90A4AE',
  color10: '#F06292',
};

const EMPTY_FORM: EncounterTypeFormData = {
  encounter_type: '',
  color: 'color1',
  default_duration: null,
  default_modality: null,
  default_cpt_codes: null,
  default_icd10_codes: null,
  default_place_of_service: null,
  telehealth: false,
  template_id: null,
};

function ColorDot({ color }: { color: string }) {
  const hex = COLOR_MAP[color] || '#9E9E9E';
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: hex,
        display: 'inline-block',
        verticalAlign: 'middle',
        mr: 1,
      }}
    />
  );
}

function EncounterTypeFormFields({
  form,
  onChange,
  templates,
}: {
  form: EncounterTypeFormData;
  onChange: (update: Partial<EncounterTypeFormData>) => void;
  templates: EffectiveNoteTemplateSummary[];
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <TextField
        label="Name"
        value={form.encounter_type || ''}
        onChange={(e) => onChange({ encounter_type: e.target.value })}
        fullWidth
        required
      />

      <FormControl fullWidth>
        <InputLabel>Color</InputLabel>
        <Select
          value={form.color || ''}
          label="Color"
          onChange={(e) => onChange({ color: e.target.value })}
        >
          {Object.entries(COLOR_MAP).map(([key, hex]) => (
            <MenuItem key={key} value={key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: hex }} />
                {key}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Default Duration (minutes)"
        type="number"
        value={form.default_duration ?? ''}
        onChange={(e) =>
          onChange({ default_duration: e.target.value ? Number(e.target.value) : null })
        }
        fullWidth
      />

      <FormControl fullWidth>
        <InputLabel>Default Modality</InputLabel>
        <Select
          value={form.default_modality || ''}
          label="Default Modality"
          onChange={(e) =>
            onChange({
              default_modality: (e.target.value || null) as EncounterTypeUpdate['default_modality'],
            })
          }
        >
          <MenuItem value="">None</MenuItem>
          <MenuItem value="in_person">In Person</MenuItem>
          <MenuItem value="video">Video</MenuItem>
          <MenuItem value="audio">Audio</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Default CPT Codes (comma-separated)"
        value={form.default_cpt_codes?.join(', ') || ''}
        onChange={(e) =>
          onChange({
            default_cpt_codes: e.target.value
              ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
              : null,
          })
        }
        fullWidth
      />

      <TextField
        label="Default ICD-10 Codes (comma-separated)"
        value={form.default_icd10_codes?.join(', ') || ''}
        onChange={(e) =>
          onChange({
            default_icd10_codes: e.target.value
              ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
              : null,
          })
        }
        fullWidth
      />

      <TextField
        label="Default Place of Service"
        value={form.default_place_of_service || ''}
        onChange={(e) => onChange({ default_place_of_service: e.target.value || null })}
        fullWidth
        helperText="CMS place of service code (e.g., '11', '10', '02')"
      />

      <FormControl fullWidth>
        <InputLabel>Template</InputLabel>
        <Select
          value={form.template_id || ''}
          label="Template"
          onChange={(e) => onChange({ template_id: e.target.value || null })}
        >
          <MenuItem value="">None</MenuItem>
          {templates.map((t) => (
            <MenuItem key={t.template?.id} value={t.template?.id || ''}>
              {t.revision?.title || 'Unnamed Template'}
              {t.template?.owner_scope ? ` (${t.template.owner_scope})` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={form.telehealth ?? false}
            onChange={(e) => onChange({ telehealth: e.target.checked })}
          />
        }
        label="Telehealth"
      />
    </Box>
  );
}

export function ProviderEncounterTypesSection({
  providerId,
  templates = [],
}: ProviderEncounterTypesSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<EncounterTypeResponse | null>(null);
  const [form, setForm] = useState<EncounterTypeFormData>(EMPTY_FORM);

  const {
    data: encounterTypesData,
    isLoading,
    error,
  } = useGetAdminProviderEncounterTypesQuery({ providerId });

  const [createEncounterType, { isLoading: isCreating }] =
    useCreateAdminProviderEncounterTypeMutation();
  const [updateEncounterType, { isLoading: isUpdating }] =
    useUpdateAdminProviderEncounterTypeMutation();
  const [deleteEncounterType, { isLoading: isDeleting }] =
    useDeleteAdminProviderEncounterTypeMutation();

  const isSaving = isCreating || isUpdating;
  const encounterTypes = encounterTypesData?.items || [];

  const templateMap = new Map(
    templates.map((t) => [t.template?.id, t.revision?.title || 'Unnamed Template']),
  );
  const getTemplateName = (templateId: string | null | undefined) =>
    templateId ? templateMap.get(templateId) || templateId.substring(0, 12) + '...' : '-';

  const handleFormChange = useCallback((update: Partial<EncounterTypeFormData>) => {
    setForm((prev) => ({ ...prev, ...update }));
  }, []);

  const handleOpenCreate = useCallback(() => {
    setSelectedType(null);
    setForm(EMPTY_FORM);
    setDialogMode('create');
  }, []);

  const handleOpenEdit = useCallback((et: EncounterTypeResponse) => {
    setSelectedType(et);
    setForm({
      encounter_type: et.encounter_type,
      color: et.color,
      default_duration: et.default_duration,
      default_modality: et.default_modality,
      default_cpt_codes: et.default_cpt_codes,
      default_icd10_codes: et.default_icd10_codes,
      default_place_of_service: et.default_place_of_service,
      telehealth: et.telehealth,
      template_id: et.template_id,
    });
    setDialogMode('edit');
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogMode(null);
    setSelectedType(null);
    setForm(EMPTY_FORM);
  }, []);

  const handleSave = useCallback(async () => {
    if (!form.encounter_type?.trim()) {
      enqueueSnackbar('Name is required', { variant: 'warning' });
      return;
    }

    // Check for duplicate name (case-insensitive), excluding current type when editing
    const trimmedName = form.encounter_type.trim().toLowerCase();
    const isDuplicate = encounterTypes.some(
      (et) =>
        et.encounter_type.toLowerCase() === trimmedName &&
        (dialogMode !== 'edit' || et.encounter_type_id !== selectedType?.encounter_type_id),
    );
    if (isDuplicate) {
      enqueueSnackbar(`An encounter type named "${form.encounter_type.trim()}" already exists`, { variant: 'warning' });
      return;
    }

    try {
      if (dialogMode === 'create') {
        await createEncounterType({
          providerId,
          encounterTypeCreate: {
            encounter_type: form.encounter_type.trim(),
            color: form.color || undefined,
            default_duration: form.default_duration,
            default_modality: form.default_modality,
            default_cpt_codes: form.default_cpt_codes,
            default_icd10_codes: form.default_icd10_codes,
            default_place_of_service: form.default_place_of_service,
            telehealth: form.telehealth,
            template_id: form.template_id,
          } as EncounterTypeCreate,
        }).unwrap();
        enqueueSnackbar('Encounter type created', { variant: 'success' });
      } else if (selectedType) {
        await updateEncounterType({
          providerId,
          encounterTypeId: selectedType.encounter_type_id,
          encounterTypeUpdate: form as EncounterTypeUpdate,
        }).unwrap();
        enqueueSnackbar('Encounter type updated', { variant: 'success' });
      }
      handleCloseDialog();
    } catch (err: any) {
      enqueueSnackbar(
        err?.data?.message || err?.data?.detail || `Failed to ${dialogMode} encounter type`,
        { variant: 'error' },
      );
    }
  }, [dialogMode, form, selectedType, providerId, createEncounterType, updateEncounterType, enqueueSnackbar, handleCloseDialog]);

  const handleOpenDelete = useCallback((et: EncounterTypeResponse) => {
    setSelectedType(et);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedType(null);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!selectedType) return;
    try {
      await deleteEncounterType({
        providerId,
        encounterTypeId: selectedType.encounter_type_id,
      }).unwrap();
      enqueueSnackbar('Encounter type deleted', { variant: 'success' });
      handleCloseDelete();
    } catch (err: any) {
      enqueueSnackbar(
        err?.data?.message || err?.data?.detail || 'Failed to delete encounter type',
        { variant: 'error' },
      );
    }
  }, [selectedType, providerId, deleteEncounterType, enqueueSnackbar, handleCloseDelete]);

  if (isLoading) {
    return <LoadingState message="Loading encounter types..." />;
  }

  if (error) {
    return (
      <Typography color="error">
        Failed to load encounter types.
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={handleOpenCreate}
        >
          Add Encounter Type
        </Button>
      </Box>

      {encounterTypes.length === 0 ? (
        <Typography color="text.secondary">
          No encounter types configured for this provider.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Modality</TableCell>
              <TableCell>CPT Codes</TableCell>
              <TableCell>ICD-10 Codes</TableCell>
              <TableCell>Telehealth</TableCell>
              <TableCell>Template</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {encounterTypes.map((et) => (
              <TableRow key={et.encounter_type_id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ColorDot color={et.color} />
                    <Typography variant="body2" fontWeight={500}>
                      {et.encounter_type}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {et.default_duration ? `${et.default_duration} min` : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {et.default_modality ? MODALITY_LABELS[et.default_modality] || et.default_modality : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {et.default_cpt_codes?.length ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {et.default_cpt_codes.map((code) => (
                        <Chip key={code} label={code} size="small" variant="outlined" />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {et.default_icd10_codes?.length ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {et.default_icd10_codes.map((code) => (
                        <Chip key={code} label={code} size="small" variant="outlined" />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Badge color={et.telehealth ? 'success' : 'default'}>
                    {et.telehealth ? 'Yes' : 'No'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {et.template_id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2">
                        {getTemplateName(et.template_id)}
                      </Typography>
                      <Tooltip title="Copy Template ID">
                        <IconButton
                          size="small"
                          onClick={() => {
                            void navigator.clipboard.writeText(et.template_id || '');
                          }}
                        >
                          <ContentCopy sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpenEdit(et)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleOpenDelete(et)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogMode !== null} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add Encounter Type' : 'Edit Encounter Type'}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <EncounterTypeFormFields
            form={form}
            onChange={handleFormChange}
            templates={templates}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            {isSaving ? 'Saving...' : dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDelete} maxWidth="xs">
        <DialogTitle>Delete Encounter Type</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedType?.encounter_type}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
